import isEmpty from "lodash/isEmpty";
import { useActivityTemplateType } from "PFApp/activities/hooks";
import { useBookingEditModeStoreActions } from "PFApp/booking/booking_edit_mode_store";
import { useFutureBookings } from "PFApp/booking/hooks/use_future_bookings";
import {
  useReassignActionApi,
  useReassignActionState
} from "PFApp/booking/parts/overview/booking_module_calendar/context/reassign_action_context";
import { ProfileFrom } from "PFApp/booking/parts/overview/booking_module_calendar/context/reassign_action_context_provider";
import { useRequestApprovalSubmit } from "PFApp/booking/parts/overview/booking_module_calendar/context/use_request_approval_submit";
import { areRequestApprovalDataFilled } from "PFApp/booking/parts/overview/booking_module_calendar/context/use_request_approval_submit.utils";
import { useBookingModuleStoreActions } from "PFApp/booking/store";
import { EditMode } from "PFApp/booking/types/edit_mode";
import { Icon } from "PFComponents/icon";
import { LoadingDots } from "PFComponents/loading_dots";
import { Modal } from "PFComponents/modal";
import { Typography } from "PFComponents/typography";
import { usePotentialExceptions } from "PFCore/hooks/queries/audit_planner/exception/use_potential_exceptions";
import { BasicProfile, MatchScores, Profile } from "PFTypes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useReassignSubmit } from "../../parts/overview/booking_module_calendar/context/use_reassign_submit";
import { ApprovalSection } from "./approval_section";
import { BookingsToReassignSelector } from "./bookings_to_reassign_selector";
import { ReassignSelectOption } from "./bookings_to_reassign_selector.types";
import { ProfileItemDisplay } from "./profile_item_display";
import css from "./reassign_booking_modal.module.scss";

export type SelectedUserData = {
  profile: Profile | undefined;
  scores?: MatchScores;
  isUnbiasedMode?: boolean;
};

type ReassignBookingModalProps = {
  profileFrom?: ProfileFrom;
  profileTo?: SelectedUserData;
  isLoading?: boolean;
};

export const ReassignBookingModal = ({ profileFrom, profileTo, isLoading }: ReassignBookingModalProps) => {
  const { t } = useTranslation("bookingModule");

  const { data } = useReassignActionState();

  const { setEditMode } = useBookingEditModeStoreActions();
  const { setHeaderDisabled } = useBookingModuleStoreActions();

  const [reassignOption, setReassignOption] = useState(ReassignSelectOption.ThisBooking);
  const [note, setNote] = useState("");
  const [finalApprover, setFinalApprover] = useState<BasicProfile | null>(null);

  const templateId = data?.activity?.template_id ?? data?.bookings[0]?.activity?.template_id;
  const { isRole, isAudit } = useActivityTemplateType({
    template_id: templateId
  });

  const isAuditRole = isRole && isAudit;

  const { closeModal } = useReassignActionApi();
  const { handleSubmit } = useReassignSubmit({
    onError: data?.onError,
    onSubmit: data?.onSubmit,
    sourceProfileIds: data ? [data.sourceProfileId] : [],
    defaultTargetProfileId: data?.targetProfileId,
    bookings: data?.bookings ?? [],
    bulkSelection: data?.bulkSelection,
    reassignTarget: data?.reassignTarget
  });

  const activityId = data?.bookings[0]?.activity_id ?? undefined;

  const currentBookings = useMemo(
    () =>
      (data?.bookings ?? []).map((booking) => ({
        ...booking,
        startDate: booking.start_date,
        endDate: booking.end_date
      })),
    [data]
  );

  const { futureBookings } = useFutureBookings({
    thisBooking: { id: currentBookings[0]?.id ?? -1, startDate: currentBookings[0]?.start_date ?? "" },
    activityId,
    profileId: profileFrom?.id as number
  });

  const bookingsIds = useMemo(() => {
    if (isEmpty(currentBookings)) {
      return [];
    }

    switch (reassignOption) {
      case ReassignSelectOption.ThisBooking:
        return currentBookings.map(({ id }) => id) ?? [];
      case ReassignSelectOption.ThisAndFuture:
        if (futureBookings) {
          return futureBookings.map(({ id }) => id);
        }
        return undefined;
      case ReassignSelectOption.AllBookings:
        return undefined;
    }
  }, [currentBookings, reassignOption, futureBookings]);

  const { data: potentialExceptions } = usePotentialExceptions({
    params: {
      profileId: profileTo?.profile?.id,
      activityId,
      bookingsIds
    }
  });

  const { requestApprovalSubmit } = useRequestApprovalSubmit({
    currentBookings,
    activityId,
    profileId: profileFrom?.id
  });

  const requiresApproval = isAuditRole && !!potentialExceptions;

  if (!data || isEmpty(currentBookings)) {
    return null;
  }

  const roleName = data.activityName ?? "";

  const handleClose = () => {
    data?.onClose && data.onClose();
    closeModal();
  };

  const handleOk = () => {
    if (requiresApproval) {
      requestApprovalSubmit({
        currentBookings,
        activityId,
        profileFrom,
        profileTo: profileTo?.profile,
        finalApprover,
        note,
        reassignOption
      });
    } else {
      handleSubmit(reassignOption);
    }
    setEditMode(EditMode.Disabled);
    setHeaderDisabled(false);
    handleClose();
  };
  const shouldDisplayReassignOptions = data.bookings.length === 1 && !!activityId;

  const labelOk = requiresApproval
    ? t("bookings.reassign.requestApprovalAction")
    : t("bookings.reassign.reassignAction");

  const isSubmitDisabled =
    requiresApproval &&
    !areRequestApprovalDataFilled({
      currentBookings,
      activityId,
      profileFrom,
      profileTo: profileTo?.profile,
      finalApprover,
      note,
      reassignOption
    });

  return (
    <Modal
      title={t("bookings.reassign.title", { roleName })}
      labelOK={labelOk}
      disableOKButton={isSubmitDisabled}
      onOK={handleOk}
      onClose={handleClose}
      classes={{ modal: css.modal }}
    >
      <div className={css.root}>
        <Typography withMargin variant="bodyRegular">
          {t("bookings.reassign.question")}
        </Typography>
        {shouldDisplayReassignOptions && !!profileFrom && (
          <BookingsToReassignSelector
            profileId={profileFrom.id}
            activityId={activityId}
            currentBooking={{
              id: currentBookings[0].id ?? -1,
              startDate: currentBookings[0].startDate,
              endDate: currentBookings[0].endDate
            }}
            reassignOptionSelected={reassignOption}
            setReassignOptionSelected={setReassignOption}
          />
        )}
        {isLoading || !profileFrom || !profileTo?.profile ? (
          <LoadingDots circlesEnabled circleSize="xs" />
        ) : (
          <div className={css.reassignContainer}>
            <ProfileItemDisplay profile={profileFrom} />
            <div>
              <Icon name="reassign" />
            </div>

            <div className={css.personToContainer}>
              <ProfileItemDisplay profile={profileTo.profile} />
            </div>
          </div>
        )}
        {requiresApproval && (
          <ApprovalSection
            exception={potentialExceptions}
            note={note}
            onNoteChange={setNote}
            approver={finalApprover}
            onApproverChange={setFinalApprover}
          />
        )}
      </div>
    </Modal>
  );
};
